import * as React from "react";
import { userHelper, WorkgroupListing, WorkgroupMember } from '../../../amorg-common';
import { Tree, TreeNode } from 'react-organizational-chart';
import styled from 'styled-components';
import { getRootNodes, WorkgroupListingNode } from './OrgChartHelper';
import { OrganizationOwner } from '../../../amorg-common';
import { P } from "../../../designSystem/typography/Body";
import { H2 } from "../../../designSystem/typography/Heading";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { colors } from "../../../designSystem/colors/color";
import { UserAvatar } from "../../../designSystem/avatar/UserAvatar";
import { Button } from "../../../designSystem/button/Button";
import { Icon } from "../../../designSystem/icons/Icons";

// https://www.npmjs.com/package/react-organizational-chart

type OrgChartProps = {
    organizationOwner: OrganizationOwner | null;
    allWorkgroups: WorkgroupListing[],
    onClickCreateNew?: () => void,
    onClickEditGroup?: (workgroupId: string) => void
    onClickEditRootGroup?: () => void,
}
export function OrgChart(props: OrgChartProps): JSX.Element {

    const rootNodes = getRootNodes(props.allWorkgroups);

    return (
        <>
            {props.allWorkgroups &&
                <Tree 
                    label={
                        <RootNode organizationOwner={props.organizationOwner} onClickEditRootGroup={props.onClickEditRootGroup} />
                    }>
                    {rootNodes.map((item) => (
                        <ChartNode
                            key={item.workgroup.id}
                            node={item}
                            onClickEditWorkgroup={props.onClickEditGroup} />
                    ))}
                </Tree>
            }
        </>
    );
}

type RootNodeProps = {
    onClickEditRootGroup?: () => void;
    organizationOwner: OrganizationOwner | null;
}
function RootNode(props: RootNodeProps): JSX.Element {
    return (
        <WorkgroupCard
            title="Organisationsansvarlig"
            subTitle={props.organizationOwner 
                ? userHelper.getPublicUserFullName(props.organizationOwner)
                : "Ingen ansvarlig registreret"
            }
            bgColor={colors.org.card.background.root}
            onClick={props.onClickEditRootGroup}>
        </WorkgroupCard>
    )
}

type ChartNodeProps = {
    node: WorkgroupListingNode,
    onClickEditWorkgroup?: (workgroupId: string) => void
}
function ChartNode(props: ChartNodeProps): JSX.Element {
    return (
        <TreeNode label={
            <WorkgroupRepresentation wg={props.node} onClickEditWorkgroup={props.onClickEditWorkgroup} /> }>
            
            {props.node.children.map((child) => (
                <ChartNode
                    key={child.workgroup.id}
                    node={child}
                    onClickEditWorkgroup={props.onClickEditWorkgroup} />
            ))}
        </TreeNode>
    );
}
type WorkgroupRepresentationProps = {
    wg: WorkgroupListingNode
    onClickEditWorkgroup?: (workgroupId: string) => void
}
function WorkgroupRepresentation(props: WorkgroupRepresentationProps): JSX.Element {

    const group = props.wg.workgroup;

    const bgcolor = props.wg.workgroup.type === 'committee'
        ? colors.org.card.background.committee
        : colors.org.card.background.workgroup;

    return (
        <WorkgroupCard
            key={group.id}
            title={group.name}
            subTitle={group.type === 'committee' ? 'Udvalg' : 'Arbejdsgruppe'}
            onClick={
                props.onClickEditWorkgroup
                    ? () => props.onClickEditWorkgroup?.(group.id)
                    : undefined
            }
            bgColor={bgcolor}>
            <div>
                {group.members.length === 0 &&
                    <P>
                        Ingen medlemmer tilføjet
                    </P>
                }

                {group.members.length > 0 && 
                    <Flex flexDirection="column" gap={4} style={{marginTop:'10px'}}>
                        {group.members.map((member) => {
                            return (
                                <MemberRow key={member.userId} member={member} />
                            )
                        })}
                    </Flex>
                }
            </div>
        </WorkgroupCard>
    )
}


type WorkgroupCardProps = {
    title:string,
    subTitle:string,
    bgColor:string,
    children?:React.ReactNode,
    onClick?:() => void
}
function WorkgroupCard(props:WorkgroupCardProps):JSX.Element
{
    return (
        <WorkgroupCardWrapper
            bgcolor={props.bgColor}>
            <div>
                {props.onClick &&
                    <span style={{position:'absolute', right: '0px', top: '0px'}}>
                        <Button variant="text" icon='navigation_settings' text="" onClick={() => props.onClick?.()} />
                    </span>
                }

                <div style={{margin:'0px 10px'}}>
                    <H2>{props.title}</H2>
                    <span style={{fontSize:14, color: colors.text.gray.normal}}>
                        {props.subTitle}
                    </span>
                </div>
                <div style={{marginTop:8}}>
                    {props.children}
                </div>
            </div>
        </WorkgroupCardWrapper>
    );
}


const WorkgroupCardWrapper = styled.div<{ bgcolor: string }>`

    display: inline-block;
    
    border-radius:${props => props.theme.styles.card.borderRadius};
    border:${props => `1px solid ${props.theme.colors.card.outlined.active.border}`};
    background-color: ${props => props.bgcolor};
    padding: 8px 16px;
    
    min-width: 200px;
    max-width: 300px;
    font-size: 0.9rem;
    background-color:${props => props.bgcolor};
    position: relative;
`;



type MemberRowProps = {
    member:WorkgroupMember
}
function MemberRow(props:MemberRowProps):JSX.Element
{
    return (
        <Flex gap={8} style={{minHeight:60}}>
            <UserAvatar
                key={props.member.userId}
                user={{
                    displayName: props.member.firstname + ' ' + props.member.lastname,
                    initials: props.member.initials
                }}
                size="32px"
                backgroundColorOverride={colors.getUserRoleColor(props.member.roleId)}
                noToolTip
            />
            <Flex flexDirection="column" style={{justifyContent:'start', alignItems:'flex-start', minWidth:0, flex:1}}>
                <span style={{textAlign:'left', display:'block', width:'100%', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                    {props.member.firstname} {props.member.lastname}
                </span>
                {props.member.roleId &&
                    <span style={{textAlign:'left', fontSize:12, display:'block', width:'100%', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                        {props.member.roleTitle}
                    </span>
                }
                <Flex alignItems="center" gap={4} style={{fontSize:12}}>
                    <a href={`mailto:${props.member.email}`} style={{display:'flex', alignItems:'center', gap:4, color: colors.text.gray.normal}}>
                        <Icon name="interface_glyph_email" size="x-small" color={colors.text.gray.normal} />
                    </a>
                    <span style={{color: colors.text.gray.normal}}>{props.member.email}</span>
                </Flex>
            </Flex>
        </Flex>
    );
}